/**
 * Typed client over the generated route table. Identity is the x-ms-client-principal header that Static Web Apps adds
 * when it proxies /api/* to the linked backend — the browser never sets it, so there is nothing to attach here.
 */
import { ROUTES, type RouteName } from './routes.generated.ts';
import type { RouteShapes, ApiErrorBody } from './types.ts';

// Compile-time guard: every generated route has a declared shape and vice versa.
type MissingShapes = Exclude<RouteName, keyof RouteShapes>;
type ExtraShapes = Exclude<keyof RouteShapes, RouteName>;
const _routesCovered: [MissingShapes, ExtraShapes] = [undefined as never, undefined as never];
void _routesCovered;

type Params<N extends RouteName> = { [K in (typeof ROUTES)[N]['params'][number]]: string };
type Query<N extends RouteName> = RouteShapes[N]['query'];
type Body<N extends RouteName> = RouteShapes[N]['body'];
export type Response<N extends RouteName> = RouteShapes[N]['response'];

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string | undefined, message: string) { super(message); }
  get unauthenticated(): boolean { return this.status === 401; }
  get stale(): boolean { return this.status === 409 && this.code === 'stale'; }
  /** The employee has not chosen their legal entity yet (POST /trips, GET /entity/fiscal-card): show the picker. */
  get entityRequired(): boolean { return this.status === 409 && this.code === 'entity-required'; }
}

export function urlFor<N extends RouteName>(name: N, params: Params<N>, query?: Query<N>): string {
  let path: string = ROUTES[name].path;
  for (const [k, v] of Object.entries(params as Record<string, string>)) path = path.replace(`{${k}}`, encodeURIComponent(v));
  const q = query ? new URLSearchParams(Object.entries(query as Record<string, string>).filter(([, v]) => v != null)).toString() : '';
  return q ? `${path}?${q}` : path;
}

export interface CallOptions<N extends RouteName> { params?: Params<N>; query?: Query<N>; body?: Body<N>; signal?: AbortSignal }

export async function call<N extends RouteName>(name: N, opts: CallOptions<N> = {}): Promise<Response<N>> {
  const route = ROUTES[name];
  const url = urlFor(name, (opts.params ?? {}) as Params<N>, opts.query);
  const init: RequestInit = { method: route.method, signal: opts.signal, headers: { Accept: 'application/json' }, credentials: 'same-origin' };
  if (opts.body instanceof FormData) init.body = opts.body;
  else if (opts.body !== undefined) { init.body = JSON.stringify(opts.body); (init.headers as Record<string, string>)['Content-Type'] = 'application/json'; }
  const res = await fetch(url, init);
  // SWA answers an unauthenticated /api call with 401, which our responseOverrides turn into a redirect to /acceso (the
  // emulator sends the login page directly); fetch follows it and lands on HTML. Either way: the session is gone.
  if (res.status === 401 || (res.redirected && /\/(acceso|\.auth\/login)/.test(res.url))) throw new ApiError(401, 'unauthenticated', 'unauthenticated');
  if (res.ok && !(res.headers.get('content-type') ?? '').includes('json')) throw new ApiError(401, 'unauthenticated', 'unauthenticated');
  const text = await res.text();
  const json = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const e = (json ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, e.code, e.error ?? `HTTP ${res.status}`);
  }
  return json as Response<N>;
}
function safeJson(t: string): unknown { try { return JSON.parse(t); } catch { return undefined; } }

/** The document route is a 302 to a short-lived download URL: the explicit download (Descargar), never through fetch. */
export const documentUrl = (receiptId: string, documentId: string) => urlFor('receipts.document', { id: receiptId, docId: documentId });
/** The same document's bytes for the in-app viewer and thumbnails (same origin, so the CSP's img-src 'self' holds): an <img src> or fetch(). */
export const documentContentUrl = (receiptId: string, documentId: string) => urlFor('receipts.documentContent', { id: receiptId, docId: documentId });

/** Multipart body for receipts.upload / receipts.images: one part per file, field name `files`. */
export function filesForm(files: File[]): FormData { const fd = new FormData(); for (const f of files) fd.append('files', f, f.name); return fd; }
